import apiV2Service from 'common/src/modules/api/api-v2.service';
import {PostDataObj} from 'common/src/modules/api/apiTypes';
import {GetContentFeedItemsResponse} from 'common/src/modules/contentFeedItems/contentFeedItems.types';
import {
  ContentDraftItem,
  UpdateDraftItemRequest,
  GenerateDraftItemsRequest,
  IsPromptGenerationNeededRequest,
  IsPromptGenerationNeededResponse,
} from 'common/src/modules/contentFeeds/contentFeeds.types';
import {JobExecutionParams} from 'common/src/modules/jobs/jobs.types';
import {handleApiError} from 'common/src/modules/errorHandling/handleApiError';

const BASE_PATH = 'ContentItems';
const FEED_BASE_PATH = 'ContentFeed';

async function getFeedItems(feedId: number) {
  return apiV2Service
    .executeGetRequest<GetContentFeedItemsResponse>(`${BASE_PATH}/Feed/${feedId}`)
    .then(({data: reponse}) => reponse);
}

async function getFeedItemsByIds(feedId: number, itemIds: string[]) {
  const {data} = await apiV2Service.executePostRequest<GetContentFeedItemsResponse>(
    `${BASE_PATH}/Feed/${feedId}/GetByIds`,
    {},
    {itemIds},
  );

  return data;
}

async function getDraftItems(feedId: number) {
  return apiV2Service
    .executeGetRequest<ContentDraftItem[]>(`${FEED_BASE_PATH}/${feedId}/draftItems`)
    .then(({data}) => data)
    .catch((error) => {
      handleApiError(error);

      return [] as ContentDraftItem[];
    });
}

async function createDraftItem(
  feedId: number,
  item: {name?: string; columnValues?: Record<string, unknown>},
) {
  return apiV2Service
    .executePostRequest<number>(`${FEED_BASE_PATH}/${feedId}/draftItems`, {}, item)
    .then((res) => res.data)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });
}

async function createDraftItemsBatch(
  feedId: number,
  items: {name?: string; columnValues?: Record<string, unknown>}[],
): Promise<number[]> {
  const {data} = await apiV2Service.executePostRequest<number[]>(
    `${FEED_BASE_PATH}/${feedId}/draftItems/batch`,
    {},
    items as unknown as PostDataObj,
  );

  return data;
}

async function updateDraftItem(feedId: number, itemId: number, request: UpdateDraftItemRequest) {
  return apiV2Service
    .executePutRequest<void>(
      `${FEED_BASE_PATH}/${feedId}/draftItems/${itemId}`,
      {},
      request as unknown as Record<string, unknown>,
    )
    .catch((error) => {
      handleApiError(error);
      throw error;
    });
}

async function deleteDraftItem(feedId: number, itemId: number) {
  return apiV2Service
    .executeDeleteRequest<void>(`${FEED_BASE_PATH}/${feedId}/draftItems/${itemId}`)
    .catch((error) => {
      handleApiError(error);
      throw error;
    });
}

async function generateDraftItems(
  feedId: number,
  request: GenerateDraftItemsRequest,
): Promise<number> {
  const {
    data: {executionId},
  } = await apiV2Service.executePostRequest<JobExecutionParams>(
    `${FEED_BASE_PATH}/${feedId}/draftItems/generate`,
    {},
    request as unknown as PostDataObj,
  );

  return executionId;
}

async function isPromptGenerationNeeded(
  feedId: number,
  request: IsPromptGenerationNeededRequest,
): Promise<IsPromptGenerationNeededResponse> {
  const {data} = await apiV2Service.executePostRequest<IsPromptGenerationNeededResponse>(
    `${FEED_BASE_PATH}/${feedId}/draftItems/is-prompt-generation-needed`,
    {},
    request as unknown as PostDataObj,
  );

  return data;
}

async function setActiveIntermediateImageIndex(
  feedId: number,
  itemId: number,
  columnKey: string,
  index: number,
): Promise<void> {
  await apiV2Service.executePutRequest<void>(
    `${FEED_BASE_PATH}/${feedId}/draftItems/${itemId}/columns/${columnKey}/active-intermediate/${index}`,
    {},
    {},
  );
}

export const contentFeedItemsApi = {
  getFeedItems,
  getFeedItemsByIds,
  getDraftItems,
  createDraftItem,
  createDraftItemsBatch,
  updateDraftItem,
  deleteDraftItem,
  generateDraftItems,
  isPromptGenerationNeeded,
  setActiveIntermediateImageIndex,
};
